import { Injectable, computed, signal } from '@angular/core';

import {
  ADMIN_FEATURE_FLAG_DEFINITIONS,
  AdminFeatureFlagKey,
  AdminFeatureFlagState,
  defaultAdminFeatureFlagState,
} from '@core/models/admin-feature-flags.model';

const STORAGE_KEY = 'admin_portal_feature_flags';

/**
 * Persists super-admin feature flags in localStorage and exposes them as signals.
 */
@Injectable({ providedIn: 'root' })
export class AdminFeatureFlagService {
  private readonly state = signal<AdminFeatureFlagState>(this.loadState());

  readonly flags = this.state.asReadonly();

  readonly definitions = ADMIN_FEATURE_FLAG_DEFINITIONS;

  isEnabled(key: AdminFeatureFlagKey): boolean {
    return this.state()[key];
  }

  enabled(key: AdminFeatureFlagKey) {
    return computed(() => this.state()[key]);
  }

  setEnabled(key: AdminFeatureFlagKey, enabled: boolean): void {
    const next = { ...this.state(), [key]: enabled };
    this.state.set(next);
    this.persistState(next);
  }

  toggle(key: AdminFeatureFlagKey): void {
    this.setEnabled(key, !this.state()[key]);
  }

  resetAll(): void {
    const defaults = defaultAdminFeatureFlagState();
    this.state.set(defaults);
    this.persistState(defaults);
  }

  private loadState(): AdminFeatureFlagState {
    const defaults = defaultAdminFeatureFlagState();
    if (typeof localStorage === 'undefined') {
      return defaults;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaults;

      const parsed = JSON.parse(raw) as Partial<AdminFeatureFlagState>;
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  }

  private persistState(state: AdminFeatureFlagState): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
}
